/**
 * publicSurface — which router owns this document load.
 *
 * TREATX-SEO-RECOVERY-S2 (2026-06):
 *   The app mounts ONE router per document load. A load whose address is a
 *   public, SEO-relevant page mounts `BrowserRouter`, so the page lives at a
 *   real URL (`/treatments/dental-implants`) that a crawler can fetch and that
 *   every in-page `<Link>` then renders as a clean `href`. Every other load —
 *   login, dashboards, admin, onboarding, payment returns, OAuth callbacks —
 *   mounts the existing `HashRouter` and behaves exactly as it did before.
 *
 *   There is no second route tree and no second routing authority: `App.js`
 *   renders the same `<Routes>` either way, and the allowlist below is the only
 *   place that decides which surface a path belongs to.
 *
 *   Why private routes stay on the hash: 31 navigation sites across 16 files
 *   (UnifiedLoginPage, LoginPage, RBACContext, utils/api interceptors, the MFA
 *   pages, SocialAuthCallback, …) navigate by assigning `window.location.hash`.
 *   Under `BrowserRouter` those assignments become inert fragment writes, so a
 *   successful login would never leave the login screen. Migrating them is a
 *   separate, larger change; it is deliberately NOT part of this stage.
 */
import { matchPath } from 'react-router-dom';

/**
 * The public surface. Every pattern here MUST already exist in `App.js`'s
 * route table — this list classifies routes, it does not declare them.
 *
 * Deliberately excluded, and therefore still hash-owned:
 *   • authentication + recovery  /login /register /invite/* /forgot-password
 *                                /reset-password /mfa/* /auth/* /portal
 *   • onboarding + applications  /provider/register /provider/onboarding
 *                                /onboarding/* /clinic/application-status
 *                                /doctor/signup /reactivate
 *   • search + results           /search /search-v2 /find-clinics /saved-searches
 *   • payment returns            /payment-success /payment-cancel /clinic/*-success
 *   • every workspace            /patient/* /clinic/* /provider/* /doctor/*
 *                                /coordinator/* /after-sales/* /salesrep/*
 *                                /accounting/* /treatx-admin/* /my-tasks /settings/*
 *   • diagnostics                /build /parity /system-status
 * `/legal/:contentKey` and `/data-deletion` are absent on purpose: they are
 * already served by their own standalone shells from `index.js`, outside both
 * routers, and must stay that way.
 */
export const PUBLIC_ROUTE_PATTERNS = [
  '/',
  // Homepage version previews. Public pages, but `noindex` since S1.
  '/v1',
  '/v2',
  '/v3',
  // Discovery
  '/treatments',
  '/treatments/:slug',
  '/clinics/:clinicId',
  // Corporate / informational
  '/about',
  '/contact',
  '/provider-benefits',
  '/health-tourism-info',
  // Public policy pages (the canonical legal documents live at /legal/<key>)
  '/terms',
  '/privacy',
  '/privacy-policy',
  '/cookie-policy',
  '/refund-policy',
  '/cookie-settings',
  '/open-source',
];

const stripQuery = (value) => String(value || '').split('?')[0].split('#')[0];

/** True when `pathname` is one of the public, clean-URL pages. */
export function isPublicPath(pathname) {
  const path = stripQuery(pathname) || '/';
  if (!path.startsWith('/')) return false;
  return PUBLIC_ROUTE_PATTERNS.some((pattern) => matchPath({ path: pattern, end: true }, path) !== null);
}

/**
 * Legacy compatibility. A shared or bookmarked PUBLIC hash URL
 * (`https://treatx.health/#/about?x=1`) is rewritten in place to its clean
 * equivalent (`https://treatx.health/about?x=1`) BEFORE React mounts, so the
 * visitor lands on the canonical URL and the public router owns the load.
 *
 * This is a browser-side `history.replaceState` — NOT an HTTP 301. It costs no
 * extra request and leaves no entry in the back stack, so it cannot loop.
 *
 * A PRIVATE hash URL is never touched: provider invitations, password resets,
 * payment returns and every link in an already-delivered email keep working
 * byte-for-byte.
 */
export function normalizeLegacyPublicHashUrl(win) {
  try {
    const w = win || (typeof window !== 'undefined' ? window : null);
    if (!w) return false;
    const hash = w.location.hash || '';
    // Only a hash ROUTE qualifies. A plain anchor (`#corporate-identity-block`)
    // or a token payload (Apple's `#id_token=…`) must be left alone.
    if (!hash.startsWith('#/')) return false;
    // Only from the shell root; a deep pathname means some other mechanism
    // already owns this URL.
    const pathname = w.location.pathname || '/';
    if (pathname !== '/' && pathname !== '/index.html') return false;

    const raw = hash.slice(1);
    const path = stripQuery(raw);
    if (!isPublicPath(path)) return false;

    const queryFromHash = raw.includes('?') ? raw.slice(raw.indexOf('?')) : '';
    const search = queryFromHash || w.location.search || '';
    w.history.replaceState({}, '', `${w.location.origin}${path}${search}`);
    return true;
  } catch (e) {
    return false; // fail-open: the hash router still renders the page
  }
}

/**
 * Which router this document load gets. Read ONCE per mount — the public
 * surface hard-navigates when a visitor crosses into private territory, so the
 * answer cannot go stale while the app is running.
 */
/**
 * Route families the PRIVATE application owns. Used only to answer one
 * question: "is this path a known private destination, or is it simply
 * unknown?" A known private path keeps the hash router (exactly as before
 * S2/S3A); an UNKNOWN path is handed to the public surface so it can render a
 * real 404 instead of silently showing the homepage.
 *
 * TREATX-SEO-RECOVERY-S3A. `test_s3a_status_correctness_contract.py` asserts
 * this list still covers every prefix in `App.js`'s NON_PUBLIC_PREFIXES, so the
 * two cannot drift apart. `/legal/...` and `/data-deletion` are intentionally
 * absent: they are served by standalone shells in `index.js` and never reach a
 * router at all.
 */
export const PRIVATE_ROUTE_PREFIXES = [
  '/login', '/register', '/portal', '/invite', '/reactivate',
  '/forgot-password', '/reset-password', '/mfa', '/auth',
  '/patient', '/clinic', '/provider', '/doctor', '/coordinator',
  '/after-sales', '/salesrep', '/agent', '/accounting', '/treatx-admin',
  '/admin', '/settings', '/my-tasks', '/saved-searches',
  '/search', '/search-v2', '/find-clinics',
  '/payment-success', '/payment-cancel', '/payments',
  '/onboarding', '/before-after', '/build', '/parity', '/system-status',
  '/offers', '/oauth',
];

export function isPrivatePath(pathname) {
  const path = stripQuery(pathname);
  if (!path || path === '/') return false;
  return PRIVATE_ROUTE_PREFIXES.some(
    (p) => path === p || path.startsWith(`${p}/`)
  );
}

let activeSurface = null;

/**
 * Remember the surface this document load mounted on. Called once by `App`;
 * read by components (the 404 route) that must behave differently on the two
 * surfaces but render from inside the shared route table. This is a cached
 * ANSWER to `resolveSurface()`, not a second authority: it is only ever set
 * from that function's return value.
 */
export function setActiveSurface(surface) {
  activeSurface = surface;
}

export function getActiveSurface() {
  return activeSurface || resolveSurface();
}

export function resolveSurface(win) {
  const w = win || (typeof window !== 'undefined' ? window : null);
  if (!w) return 'private';
  const hash = w.location.hash || '';
  if (hash.startsWith('#/')) {
    // A hash route that survived normalisation is, by definition, private.
    return isPublicPath(stripQuery(hash.slice(1))) ? 'public' : 'private';
  }
  const path = stripQuery(w.location.pathname);
  if (isPublicPath(path)) return 'public';
  // A KNOWN private destination keeps the hash router, exactly as before.
  if (isPrivatePath(path)) return 'private';
  // TREATX-SEO-RECOVERY-S3A — anything else is unknown. Unknown belongs to the
  // public surface so it can answer with a real Not Found page instead of
  // quietly rendering the homepage at HTTP 200, which is what made every typo
  // and every stale link an indexable duplicate of `/`.
  return 'public';
}
